import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  roles: string[];
  signUp: (email: string, password: string, fullName: string, displayName?: string) => Promise<{ error: Error | null; field?: string }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<string[]>([]);

  const fetchRoles = async (userId: string) => {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    setRoles(data?.map((r: any) => r.role) ?? []);
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) fetchRoles(session.user.id);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) fetchRoles(session.user.id);
      else setRoles([]);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName: string, displayName?: string): Promise<{ error: Error | null; field?: string }> => {
    try {
      const res = await supabase.functions.invoke("custom-signup", {
        body: { email, password, full_name: fullName, display_name: displayName || "" },
      });

      if (res.error) {
        return { error: new Error(res.error.message || "Erreur lors de l'inscription") };
      }

      if (res.data?.error) {
        return { error: new Error(res.data.error), field: res.data.field };
      }

      return { error: null };
    } catch (err: any) {
      return { error: new Error(err.message || "Erreur lors de l'inscription") };
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    try {
      const res = await supabase.functions.invoke("custom-reset-password", {
        body: { email },
      });

      if (res.error) {
        return { error: new Error(res.error.message || "Erreur") };
      }

      return { error: null };
    } catch (err: any) {
      return { error: new Error(err.message || "Erreur") };
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, roles, signUp, signIn, signOut, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
};
